import type { SchoolType } from '@/features/auth/types'
import type { ClassTerm, LearnerTerm, SectionTerm, Vocabulary, YearTerm } from '@/i18n/vocabulary'

/** Kinds of institution (English labels, translated). */
export const SCHOOL_TYPES: { value: SchoolType; label: string; description: string }[] = [
  { value: 'primary', label: 'Primary school', description: 'Nursery and elementary schools.' },
  {
    value: 'secondary',
    label: 'Secondary school',
    description: 'Collèges and lycées.',
  },
  {
    value: 'combined',
    label: 'Primary and secondary school',
    description: 'Groupe scolaire: primary and secondary together.',
  },
  {
    value: 'higher',
    label: 'Higher education',
    description: 'Universities, institutes, higher schools.',
  },
  {
    value: 'training',
    label: 'Training centre',
    description: 'Language schools, vocational and professional training.',
  },
]

/** Default words of each type — same as the server (SchoolType::defaultVocabulary). */
export const TYPE_VOCABULARY: Record<SchoolType, Vocabulary> = {
  primary: { learner: 'eleve', year: 'school', class: 'class', section: 'section' },
  secondary: { learner: 'eleve', year: 'school', class: 'class', section: 'section' },
  combined: { learner: 'eleve', year: 'school', class: 'class', section: 'section' },
  higher: { learner: 'etudiant', year: 'university', class: 'level', section: 'group' },
  training: { learner: 'apprenant', year: 'session', class: 'level', section: 'group' },
}

/** The words, as shown in each language (not translated through the dictionaries). */
export const WORDS: {
  learner: Record<LearnerTerm, { fr: string; en: string }>
  year: Record<YearTerm, { fr: string; en: string }>
  class: Record<ClassTerm, { fr: string; en: string }>
  section: Record<SectionTerm, { fr: string; en: string }>
} = {
  learner: {
    eleve: { fr: 'Élève', en: 'Student' },
    etudiant: { fr: 'Étudiant', en: 'Student' },
    apprenant: { fr: 'Apprenant', en: 'Learner' },
  },
  year: {
    school: { fr: 'Année scolaire', en: 'Academic year' },
    university: { fr: 'Année universitaire', en: 'Academic year' },
    session: { fr: 'Session', en: 'Session' },
  },
  class: {
    class: { fr: 'Classe', en: 'Class' },
    level: { fr: 'Niveau', en: 'Level' },
    programme: { fr: 'Filière', en: 'Programme' },
  },
  section: {
    section: { fr: 'Section', en: 'Section' },
    group: { fr: 'Groupe', en: 'Group' },
  },
}

export const CLASS_CYCLES = [
  { value: 'preschool', label: 'Preschool' },
  { value: 'primary', label: 'Primary' },
  { value: 'lower_secondary', label: 'Lower secondary' },
  { value: 'upper_secondary', label: 'Upper secondary' },
  { value: 'higher', label: 'Higher education' },
  { value: 'training', label: 'Training' },
] as const
export type ClassCycle = (typeof CLASS_CYCLES)[number]['value']
